import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Transaction } from '../../../core/models/transaction';
import { faBoxOpen, faCircle, faPen, faPlus, faSlidersH, faTrashAlt, faTruckMoving, faUndo } from '@fortawesome/pro-solid-svg-icons';

/** Matches a `tone-*` modifier on `.status-tag` / the timeline (`$kw-transaction-tones` in _page-shell.scss). */
export type TransactionTone = 'created' | 'updated' | 'removed' | 'restored' | 'moved' | 'adjusted' | 'neutral';

export interface TransactionStyle {
  icon: IconDefinition;
  tone: TransactionTone;
  /** Full name, as the lot timeline shows it. */
  label: string;
  /** One word for tags and filter chips on the transactions grid. */
  shortLabel: string;
}

// Keyed by the backend's TransactionType display strings (KragworksHub Services/Inventory/TransactionType.cs).
// The Item* types and HandlingUnitUpdated are raw identifiers on the backend, so they carry a display label.
const TRANSACTION_STYLES: Record<string, Omit<TransactionStyle, 'label'> & { label?: string }> = {
  'Handling Unit Created': { icon: faPlus, tone: 'created', shortLabel: 'Created' },
  'Item Added': { icon: faBoxOpen, tone: 'created', shortLabel: 'Item Added' },
  'Handling Unit Updated': { icon: faPen, tone: 'updated', shortLabel: 'Updated' },
  'HandlingUnitUpdated': { icon: faPen, tone: 'updated', label: 'Handling Unit Updated', shortLabel: 'Updated' },
  'Item Updated': { icon: faPen, tone: 'updated', shortLabel: 'Item Updated' },
  'Handling Unit Removed': { icon: faTrashAlt, tone: 'removed', shortLabel: 'Removed' },
  'Item Removed': { icon: faTrashAlt, tone: 'removed', shortLabel: 'Item Removed' },
  'Item Restored': { icon: faUndo, tone: 'restored', shortLabel: 'Restored' },
  'Movement': { icon: faTruckMoving, tone: 'moved', shortLabel: 'Moved' },
  'Quantity Adjustment': { icon: faSlidersH, tone: 'adjusted', shortLabel: 'Adjusted' },
  'ItemCreated': { icon: faPlus, tone: 'created', label: 'Created', shortLabel: 'Created' },
  'ItemUpdated': { icon: faPen, tone: 'updated', label: 'Updated', shortLabel: 'Updated' },
  'ItemDeleted': { icon: faTrashAlt, tone: 'removed', label: 'Deleted', shortLabel: 'Deleted' },
};

export function transactionStyle(transactionType: string | undefined): TransactionStyle {
  const style = TRANSACTION_STYLES[transactionType ?? ''];
  const label = style?.label ?? transactionType ?? 'Unknown';
  return {
    icon: style?.icon ?? faCircle,
    tone: style?.tone ?? 'neutral',
    label,
    shortLabel: style?.shortLabel ?? label
  };
}

export interface TransactionMove {
  label: string;
  from: string;
  to: string;
}

/** The warehouse / room / location levels a transaction changed. */
export function transactionMoves(t: Transaction): TransactionMove[] {
  return [
    { label: 'Warehouse', from: t.originalWarehouse, to: t.destinationWarehouse, changed: t.originalWarehouseId != t.destinationWarehouseId },
    { label: 'Room', from: t.originalRoom, to: t.destinationRoom, changed: t.originalRoomId != t.destinationRoomId },
    { label: 'Location', from: t.originalLocation, to: t.destinationLocation, changed: t.originalLocationId != t.destinationLocationId },
  ]
    .filter(m => m.changed)
    .map(({ label, from, to }) => ({ label, from: from || '—', to: to || '—' }));
}
